import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { fetchAvailableGolfers, updateGolferTier, syncPlayersFromOWGR, syncPlayersFromSlashGolf } from '../../components/admin/adminApiService';
import EditPlayerModal from '../../components/admin/modals/EditPlayerModal';
import toast from 'react-hot-toast';

const AdminPlayers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');
  
  // Edit player modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // Load players/golfers data
  useEffect(() => {
    const loadPlayers = async () => {
      try {
        setLoading(true);
        const data = await fetchAvailableGolfers();
        setPlayers(data || []);
      } catch (error) {
        console.error('Error loading players:', error);
        toast.error('Failed to load players');
        setPlayers([]);
      } finally {
        setLoading(false);
      }
    };

    loadPlayers();
  }, []);

  const mockPlayers = [
    { 
      id: 1, 
      name: 'Scottie Scheffler', 
      country: '🇺🇸', 
      owgr: 1, 
      tier: 'A',
      earnings: 28000000,
      status: 'active'
    },
    { 
      id: 2, 
      name: 'Xander Schauffele', 
      country: '🇺🇸', 
      owgr: 2, 
      tier: 'A',
      earnings: 12000000,
      status: 'active'
    },
    { 
      id: 3, 
      name: 'Ludvig Aberg', 
      country: '🇸🇪', 
      owgr: 34, 
      tier: 'B',
      earnings: 8500000,
      status: 'active'
    },
    { 
      id: 4, 
      name: 'Viktor Hovland', 
      country: '🇳🇴', 
      owgr: 82, 
      tier: 'C',
      earnings: 6200000,
      status: 'active'
    },
    { 
      id: 5, 
      name: 'Tommy Fleetwood', 
      country: '🇬🇧', 
      owgr: 89, 
      tier: 'C',
      earnings: 5800000,
      status: 'active'
    },
    { 
      id: 6, 
      name: 'Cameron Davis', 
      country: '🇦🇺', 
      owgr: 802, 
      tier: 'D',
      earnings: 2100000,
      status: 'active'
    },
    { 
      id: 7, 
      name: 'Thriston Lawrence', 
      country: '🇿🇦', 
      owgr: 520, 
      tier: 'D',
      earnings: 1800000,
      status: 'active'
    },
    { 
      id: 8, 
      name: 'Ben Griffin', 
      country: '🇺🇸', 
      owgr: 98, 
      tier: 'C',
      earnings: 4200000,
      status: 'active'
    }
  ];

  const filteredPlayers = players.filter(player => {
    const matchesSearch = player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (player.country && player.country.includes(searchTerm));
    // Convert tier format - real data uses 'TIER_A' format
    const playerTier = player.tier ? player.tier.replace('TIER_', '') : '';
    const matchesTier = filterTier === 'all' || playerTier === filterTier;
    return matchesSearch && matchesTier;
  });

  const getTierColor = (tier) => {
    switch (tier) {
      case 'A': return '#dc2626'; // Red
      case 'B': return '#d97706'; // Orange
      case 'C': return '#059669'; // Green
      case 'D': return '#2563eb'; // Blue
      default: return '#6b7280';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getTierStats = () => {
    const stats = { A: 0, B: 0, C: 0, D: 0 };
    players.forEach(player => {
      const tier = player.tier ? player.tier.replace('TIER_', '') : '';
      if (stats.hasOwnProperty(tier)) {
        stats[tier]++;
      }
    });
    return stats;
  };

  const formatTier = (tier) => {
    if (!tier) return 'Unknown';
    return tier.replace('TIER_', '');
  };

  const handleTierChange = async (playerId, newTier) => {
    try {
      // Convert from display format (A, B, C, D) back to database format (TIER_A, TIER_B, etc.)
      const tierValue = `TIER_${newTier}`;
      await updateGolferTier(playerId, tierValue);
      
      // Update the player in local state
      setPlayers(players.map(player => 
        player.id === playerId ? { ...player, tier: tierValue } : player
      ));
      
      toast.success('Player tier updated successfully');
    } catch (error) {
      console.error('Error updating player tier:', error);
      toast.error(error.message || 'Failed to update player tier');
    }
  };

  const handleEditPlayer = (player) => {
    setSelectedPlayer(player);
    setShowEditModal(true);
  };

  const handlePlayerUpdated = async () => {
    // Reload players data after update
    try {
      const data = await fetchAvailableGolfers();
      setPlayers(data || []);
      toast.success('Player updated successfully');
    } catch (error) {
      console.error('Error reloading players:', error);
      toast.error('Player updated but failed to refresh list');
    }
  };

  const handleSyncOWGR = async () => {
    if (window.confirm('This will sync the top 200 players from OWGR (Official World Golf Ranking). Existing players will be updated with new rankings and tiers. Continue?')) {
      setIsSyncing(true);
      setSyncStatus('Syncing players from OWGR...');
      
      try {
        const result = await syncPlayersFromOWGR();
        const message = `Successfully synced ${result.newPlayers || 0} new players and updated ${result.updatedPlayers || 0} existing players`;
        toast.success(message);
        setSyncStatus(message);
        
        // Reload players to show updated data
        const data = await fetchAvailableGolfers();
        setPlayers(data || []);
        
        // Clear status message after 5 seconds
        setTimeout(() => setSyncStatus(''), 5000);
      } catch (error) {
        console.error('Error syncing players from OWGR:', error);
        toast.error('Failed to sync players from OWGR');
        setSyncStatus('');
      } finally {
        setIsSyncing(false);
      }
    }
  };

  const handleSyncSlashGolf = async () => {
    if (window.confirm('This will sync players from SlashGolf API. Existing players will be updated with new rankings and tiers. Continue?')) {
      setIsSyncing(true);
      setSyncStatus('Syncing players from SlashGolf API...');
      
      try {
        const result = await syncPlayersFromSlashGolf();
        const message = `Successfully synced ${result.newPlayers || 0} new players and updated ${result.updatedPlayers || 0} existing players from SlashGolf API`;
        toast.success(message);
        setSyncStatus(message);
        
        // Reload players to show updated data
        const data = await fetchAvailableGolfers();
        setPlayers(data || []);
        
        // Clear status message after 5 seconds
        setTimeout(() => setSyncStatus(''), 5000);
      } catch (error) {
        console.error('Error syncing players from SlashGolf API:', error);
        toast.error('Failed to sync players from SlashGolf API');
        setSyncStatus('');
      } finally {
        setIsSyncing(false);
      }
    }
  };

  const tierStats = getTierStats();

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-players">
          <div className="admin-card">
            <div className="loading-container">
              <div className="admin-loading-spinner">
                <div className="admin-spinner"></div>
              </div>
              <p>Loading players...</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-players">
        <div className="admin-card">
          <div className="admin-card-header">
            <div>
              <h2 className="admin-card-title">Player Management</h2>
              <p className="admin-card-subtitle">Manage golf players and their tiers ({players.length} total)</p>
            </div>
            <button
              className="admin-btn admin-btn-primary"
              onClick={handleSyncOWGR}
              disabled={isSyncing}
            >
              {isSyncing ? 'Syncing...' : 'Sync from OWGR'}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="admin-card">
          <div className="admin-filters">
            <div className="admin-form-group">
              <label className="admin-form-label">Search Players</label>
              <input
                type="text"
                className="admin-form-input"
                placeholder="Search by name or country..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Filter by Tier</label>
              <select
                className="admin-form-select"
                value={filterTier}
                onChange={(e) => setFilterTier(e.target.value)}
              >
                <option value="all">All Tiers</option>
                <option value="A">A Tier</option>
                <option value="B">B Tier</option>
                <option value="C">C Tier</option>
                <option value="D">D Tier</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sync Status */}
        {syncStatus && (
          <div className="admin-card">
            <div className="admin-notification admin-notification-success">
              <div className="admin-notification-content">
                <span className="admin-notification-icon">✅</span>
                <span>{syncStatus}</span>
              </div>
            </div>
          </div>
        )}

        {/* Players Table */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Players ({filteredPlayers.length})</h3>
            <div className="admin-card-actions">
              <button 
                className="admin-btn admin-btn-secondary"
                onClick={handleSyncOWGR}
                disabled={isSyncing}
                style={{ marginRight: '8px' }}
                title="Import players from Official World Golf Ranking"
              >
                {isSyncing ? '🔄 Syncing...' : '📥 Import from OWGR'}
              </button>
              <button 
                className="admin-btn admin-btn-secondary"
                onClick={handleSyncSlashGolf}
                disabled={isSyncing}
                style={{ marginRight: '8px' }}
                title="Import players from SlashGolf API"
              >
                {isSyncing ? '🔄 Syncing...' : '🏌️ SlashGolf-API'}
              </button>
              <button className="admin-btn admin-btn-primary">
                ➕ Add Player
              </button>
            </div>
          </div>
          
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Country</th>
                  <th>OWGR</th>
                  <th>Tier</th>
                  <th>Events</th>
                  <th>Total Points</th>
                  <th>Avg Points</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlayers.map((player) => (
                  <tr key={player.id}>
                    <td>{player.id}</td>
                    <td>
                      <div className="admin-player-name">
                        {player.name}
                      </div>
                    </td>
                    <td>{player.country || 'N/A'}</td>
                    <td>
                      <span className="admin-owgr-rank">
                        #{player.worldRanking || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <select
                        className="admin-form-select admin-inline-select"
                        value={formatTier(player.tier)}
                        onChange={(e) => handleTierChange(player.id, e.target.value)}
                        style={{ 
                          backgroundColor: getTierColor(formatTier(player.tier)),
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          fontSize: '12px'
                        }}
                      >
                        <option value="A">A Tier</option>
                        <option value="B">B Tier</option>
                        <option value="C">C Tier</option>
                        <option value="D">D Tier</option>
                      </select>
                    </td>
                    <td>
                      <span className="admin-events">
                        {player.events || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <span className="admin-total-points">
                        {player.totalPoints ? parseFloat(player.totalPoints).toFixed(2) : 'N/A'}
                      </span>
                    </td>
                    <td>
                      <span className="admin-avg-points">
                        {player.avgPoints ? parseFloat(player.avgPoints).toFixed(2) : 'N/A'}
                      </span>
                    </td>
                    <td>
                      <span 
                        className="admin-badge"
                        style={{ backgroundColor: player.isActive ? '#059669' : '#6b7280' }}
                      >
                        {player.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="admin-actions">
                        <button 
                          className="admin-btn admin-btn-small admin-btn-secondary"
                          onClick={() => handleEditPlayer(player)}
                          title="Edit Player"
                        >
                          Edit
                        </button>
                        <button className="admin-btn admin-btn-small admin-btn-secondary">
                          View
                        </button>
                        <button className="admin-btn admin-btn-small admin-btn-danger">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Player Stats by Tier */}
        <div className="admin-stats">
          <div className="admin-stat-card">
            <div className="admin-stat-icon">🏆</div>
            <div className="admin-stat-value">{players.length}</div>
            <div className="admin-stat-label">Total Players</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon" style={{ color: '#dc2626' }}>A</div>
            <div className="admin-stat-value">{tierStats.A}</div>
            <div className="admin-stat-label">A Tier Players</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon" style={{ color: '#d97706' }}>B</div>
            <div className="admin-stat-value">{tierStats.B}</div>
            <div className="admin-stat-label">B Tier Players</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon" style={{ color: '#059669' }}>C</div>
            <div className="admin-stat-value">{tierStats.C}</div>
            <div className="admin-stat-label">C Tier Players</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon" style={{ color: '#2563eb' }}>D</div>
            <div className="admin-stat-value">{tierStats.D}</div>
            <div className="admin-stat-label">D Tier Players</div>
          </div>
        </div>
      </div>

      {/* Edit Player Modal */}
      <EditPlayerModal
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        player={selectedPlayer}
        onPlayerUpdated={handlePlayerUpdated}
      />
    </AdminLayout>
  );
};

export default AdminPlayers;
